import * as Immutable from 'immutable';
import * as t from 'io-ts';

import { ioTsEmail, ioTsRequired } from '../../shared/helpers/io-ts-helpers';
import { Action, ImmutableInterface } from '../../types/';

export interface AccountAction {
  data?: any;
  isConsultant?: boolean;
  field?: any;
  name?: string;
  value?: any;
  type: string;
  detail?: string;
  currentFieldSaved?: any;
  json?: any;
}

interface AccountSubmittedAction {
  locked: boolean;
  approved: boolean;
}

export interface AccountDataFieldsElementStore {
  value: string | number | AccountDataFieldsElementStore;
  errors: string[];
  valid: boolean;
}
export interface BaseAccountDataFieldsStore {
  email: AccountDataFieldsElementStore;
  firstName: AccountDataFieldsElementStore;
  lastName: AccountDataFieldsElementStore;
  fullName: AccountDataFieldsElementStore;
  telephone: AccountDataFieldsElementStore;
  allowSMS: AccountDataFieldsElementStore;
  password: AccountDataFieldsElementStore;
  confirmation: AccountDataFieldsElementStore;
  captcha: AccountDataFieldsElementStore;
  agree: AccountDataFieldsElementStore;
  country: AccountDataFieldsElementStore;
  city: AccountDataFieldsElementStore;
  university: AccountDataFieldsElementStore;
  degree: AccountDataFieldsElementStore;
  major: AccountDataFieldsElementStore;
  graduationYear: AccountDataFieldsElementStore;
  stem: AccountDataFieldsElementStore;
  presently_employed: AccountDataFieldsElementStore;
  employer: AccountDataFieldsElementStore;
  business: AccountDataFieldsElementStore;
  title: AccountDataFieldsElementStore;
  description: AccountDataFieldsElementStore;
  nonPublicInformation: AccountDataFieldsElementStore;
  participationPermitted: AccountDataFieldsElementStore;
  finance: AccountDataFieldsElementStore;
  investmentAdvisory: AccountDataFieldsElementStore;
  brokerage: AccountDataFieldsElementStore;
  fundamentalResearch: AccountDataFieldsElementStore;
  proprietaryTrading: AccountDataFieldsElementStore;
  serviceEngagement: AccountDataFieldsElementStore;
  employerServices: AccountDataFieldsElementStore;
  exposureStatus: AccountDataFieldsElementStore;
  exposureDetails: AccountDataFieldsElementStore;
  referrer: AccountDataFieldsElementStore;
}
export const AccountMetadataChoices = t.interface({
  label: t.string,
  value: t.string,
});

export const AccountMetadataGraduationYear = t.interface({
  maxValue: t.number,
  minValue: t.number,
});

type AccountMetadataChoicesStatic = t.TypeOf<typeof AccountMetadataChoices>;
type AccountMetadataGraduationYearStatic = t.TypeOf<typeof AccountMetadataGraduationYear>;

export const AccountMetadataValidation = t.interface({
  actions: t.interface({
    POST: t.interface({
      gender: t.interface({
        choices: t.array(AccountMetadataChoices),
      }),
      address: t.interface({
        children: t.interface({
          country: t.interface({
            choices: t.array(AccountMetadataChoices),
          }),
        }),
      }),
      education: t.interface({
        children: t.interface({
          degree: t.interface({
            choices: t.array(AccountMetadataChoices),
          }),
          university: t.interface({
            choices: t.array(AccountMetadataChoices),
          }),
          graduationYear: AccountMetadataGraduationYear,
        }),
      }),
    }),
  }),
});

export interface CountryOptions {
  type: string;
  required: boolean;
  readOnly: boolean;
  label: string;
  choices: CountryOption[];
}

export interface CountryOption {
  value: string;
  label: string;
}

export interface BaseAccountMetadata {
  country: Immutable.List<AccountMetadataChoicesStatic>;
  degree: Immutable.List<AccountMetadataChoicesStatic>;
  university: Immutable.List<AccountMetadataChoicesStatic>;
  graduationYear: AccountMetadataGraduationYearStatic;
}

export interface BaseAccountTerms {
  terms_understand: boolean;
  terms_compensation: boolean;
  terms_authority: boolean;
}

export type AccountTerms = ImmutableInterface<BaseAccountTerms>;
export type AccountMetadata = ImmutableInterface<BaseAccountMetadata>;
export type AccountDataFieldsStore = ImmutableInterface<BaseAccountDataFieldsStore>;

interface FormTypes {
  [key: string]: {
    fields: string[];
    valid: boolean;
  };
}

export interface BaseAccountStore {
  terms: AccountTerms;
  verified: boolean;
  submitted: boolean;
  metadata: AccountMetadata;
  isFetching: boolean;
  locked: boolean;
  approved: boolean;
  dataFields: AccountDataFieldsStore;
  termsAccepted: boolean;
  hasUserOptions: boolean;
  isValidToSubmit: boolean;
  isFetchUserOptions: boolean;
  userOptionsFailure: boolean;
  isAcceptingAgreements: boolean;
  currentForm: string;
  accountType: 'create' | 'update' | 'changeEmail';
  createForm: FormTypes;
  updateForm: FormTypes;
  changeEmailForm: FormTypes;
  fetchingToken: boolean;
  fetchingWorkdayToken: boolean;
  changedEmail: boolean;
  unverifiedEmail: string;
  verifiedType: string;
  accountVerifyEmail500: boolean;
  countryOptionsMap: Immutable.Map<string, string>;
  referrer: boolean;
  isAccountDetailsAgree: boolean;
  isAgreedAlphathonUpdateAccountDetails: boolean;
  dirtyFields: Immutable.List<string>;
  recertificationProgress: Immutable.List<number>;
  competition: any;
}

export type AccountStoreElement = ImmutableInterface<BaseAccountStore>;

export interface BaseAccountStoreProps {
  account: BaseAccountStore;
}

export type AccountStore = ImmutableInterface<BaseAccountStore>;
export type AccountActionType = Action<AccountAction>;
export type AccountSubmittedActionType = Action<AccountSubmittedAction>;

export interface BaseAccountAddress {
  country: string | null;
  street: string | null;
  city: string | null;
}

export interface BaseAccountAddressCreate {
  country: string | null;
}

export interface AccountAuxiliaryCampaign {
  source: string;
  medium?: string | null;
  content?: string | null;
  term?: string | null;
  campaign: string;
}

export interface BaseAccountAuxiliary {
  agree: string[] | null;
  password: string | null;
  confirmation: string | null;
  captcha: string | null;
  campaign?: null | AccountAuxiliaryCampaign;
}

export interface BaseAccountEducation {
  university: string | null;
  degree: string | null;
  major: string | null;
  graduationYear: string | null;
  gpa: number | null;
  maxGPA: number | null;
  stem: boolean | null;
}

export interface BaseAccountEmployment {
  employer: string | null;
  business: string | null;
  title: string | null;
  description: string | null;
  nonPublicInformation: boolean | null;
  participationPermitted: boolean | null;
  finance: boolean | null;
  investmentAdvisory: boolean | null;
  brokerage: boolean | null;
  fundamentalResearch: boolean | null;
  proprietaryTrading: boolean | null;
  serviceEngagement: boolean | null;
  employerServices: string | null;
  exposureStatus: boolean;
  exposureDetails: string | null;
}

export interface BaseCreateAccountData {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  address: BaseAccountAddressCreate;
  auxiliary: BaseAccountAuxiliary;
  employment: BaseAccountEmployment | null;
}

export const UserData = t.interface({
  firstName: ioTsRequired,
  lastName: ioTsRequired,
  telephone: t.union([t.string, t.null]),
  email: t.intersection([ioTsRequired, ioTsEmail]),
  settings: t.interface({
    communication: t.interface({
      allowSMS: t.boolean,
    }),
  }),
});

export const UserDataCreate = t.interface({
  firstName: ioTsRequired,
  lastName: ioTsRequired,
  email: t.intersection([ioTsRequired, ioTsEmail]),
});

export const UserAuxiliary = t.interface({
  auxiliary: t.interface({
    password: t.string,
    confirmation: t.string,
    captcha: t.union([t.string, t.null]),
    agree: t.array(t.string),
    campaign: t.union([
      t.null,
      t.partial({
        campaign: t.union([t.string, t.null]),
        content: t.union([t.string, t.null]),
        medium: t.union([t.string, t.null]),
        source: t.union([t.string, t.null]),
      }),
    ]),
  }),
});

export const UserAddress = t.interface({
  address: t.interface({
    country: ioTsRequired,
    city: t.union([t.string, t.null]),
    state: t.union([t.string, t.null]),
  }),
});

export const UserAddressCreate = t.interface({
  address: t.interface({
    country: ioTsRequired,
  }),
});

export const UserEducation = t.interface({
  education: t.interface({
    university: t.union([t.string, t.null]),
    degree: t.union([t.string, t.null]),
    major: t.union([t.string, t.null]),
    graduationYear: t.union([t.number, t.null]),
    stem: t.union([t.boolean, t.null]),
    gpa: t.union([t.number, t.null]),
    maxGPA: t.union([t.number, t.null]),
  }),
});

export const UserEmploymentOptions = t.interface({
  employer: t.union([t.string, t.null]),
  business: t.union([t.string, t.null]),
  title: t.union([t.string, t.null]),
  description: t.union([t.string, t.null]),
  nonPublicInformation: t.union([t.boolean, t.null]),
  participationPermitted: t.union([t.boolean, t.null]),
  finance: t.union([t.boolean, t.null]),
  investmentAdvisory: t.union([t.boolean, t.null]),
  brokerage: t.union([t.boolean, t.null]),
  fundamentalResearch: t.union([t.boolean, t.null]),
  proprietaryTrading: t.union([t.boolean, t.null]),
  serviceEngagement: t.union([t.boolean, t.null]),
  employerServices: t.union([t.string, t.null]),
  exposureStatus: t.boolean,
  exposureDetails: t.union([t.string, t.null]),
});

export const UserEmployment = t.interface({
  employment: t.union([UserEmploymentOptions, t.null]),
});

export const UserFullName = t.partial({
  fullName: t.union([t.string, t.null]),
});

export const UserReferral = t.partial({
  referrer: t.union([t.string, t.null]),
});

export const UserAccountCreate = t.intersection([UserDataCreate, UserFullName, UserAuxiliary, UserAddressCreate, UserEmployment]);
